import React, { useState, useEffect, useContext, useMemo } from "react"; // Added useMemo
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { AppContext } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Text } from "../components/catalyst/text";
import { Checkbox } from "../components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Header } from "../layout/Header";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  UserPlus,
  Check,
  RefreshCw,
  UserMinus,
  Loader2,
  ArrowUpDown,
  Clock,
  CheckSquare,
  Square,
  Bookmark,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import FeedbackComponent from "../components/FeedbackComponent";
import MapSection from "../components/MapSection";
import AllSchedulesMapSection from "../components/AllSchedulesMapSection";
import { ScrollArea } from "../components/ui/scroll-area";
import { cn } from "../utils/cn";
import { Separator } from "../components/ui/separator";
import AssignRsmModal from "../components/AssignRsmModal";
import { toast } from "../components/ui/use-toast";
import SaveScheduleButton from "../components/SaveScheduleButton";
import useSavedSchedules from "../hooks/useSavedSchedules";
import { format } from "date-fns";
import { formatDate } from "../utils/dateUtils";

function ClusterListPage() {
  const {
    data: contextData,
    setData: setContextData,
    loading: appLoading,
    error: appError,
    setError: setAppError,
    setLoading: setAppLoading,
    selectedClusterId,
    setSelectedClusterId,
    clusterGroupingsFeedback,
    setClusterGroupingsFeedback,
  } = useContext(AppContext);

  const { user } = useAuth();
  const isBM = user?.role === "BM";

  const navigate = useNavigate();
  const [sortDirection, setSortDirection] = useState("desc");
  const [stopsSortDirection, setStopsSortDirection] = useState(null); // Add state for stops sorting
  const [displayData, setDisplayData] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedScheduleForAssign, setSelectedScheduleForAssign] =
    useState(null);
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [actionTargetId, setActionTargetId] = useState(null);
  
  // Delete schedule state
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  
  // Bulk selection state
  const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState(new Set());
  const [bulkSaveLoading, setBulkSaveLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);


  const { toggleSaveSchedule } = useSavedSchedules();

  // <<< ADDED useMemo for feedbackContext >>>
  // Create context object for feedback, including the currently selected schedule ID
  const feedbackContext = useMemo(
    () => ({
      scheduleId: selectedClusterId, // Use the selectedClusterId from context
    }),
    [selectedClusterId],
  );

  useEffect(() => {
    const fetchRsmSchedules = async () => {
      setLocalLoading(true);
      setLocalError(null);
      setAppError(null);
      try {
        const response = await axios.get("/schedules");
        setDisplayData(sortData(response.data || [], sortDirection, stopsSortDirection));
        if (response.data && response.data.length > 0 && !selectedClusterId) {
          setSelectedClusterId(response.data[0].cluster);
        }
      } catch (err) {
        console.error("Error fetching RSM schedules:", err);
        const errorMsg =
          err.response?.data?.error ||
          err.message ||
          "Failed to load assigned schedules";
        setLocalError(errorMsg);
        setAppError(errorMsg);
        setDisplayData([]);
      } finally {
        setLocalLoading(false);
      }
    };

    if (!isBM && user) {
      fetchRsmSchedules();
    } else if (isBM) {
      // Use contextData for BM, sorting it
      console.log("BM contextData:", contextData);
      console.log("BM contextData type:", typeof contextData);
      console.log("BM contextData is array:", Array.isArray(contextData));
      console.log("BM contextData length:", Array.isArray(contextData) ? contextData.length : 'Not an array');
      
      const dataToDisplay = Array.isArray(contextData) ? contextData : [];
      console.log("Data to display count:", dataToDisplay.length);
      
      const sortedData = sortData(dataToDisplay, sortDirection, stopsSortDirection);
      console.log("Sorted data count:", sortedData.length);
      setDisplayData(sortedData);
      
      // Select first cluster from context data if none selected
      if (dataToDisplay.length > 0 && !selectedClusterId) {
        const firstId = dataToDisplay[0].id || dataToDisplay[0].cluster;
        console.log("Setting first cluster ID:", firstId);
        setSelectedClusterId(firstId);
      }
      // Sync loading/error state from context for BM
      setLocalLoading(appLoading);
      setLocalError(appError);
    }
    // Ensure selectedClusterId is valid for the current displayData
    if (
      selectedClusterId &&
      !displayData.some(
        (item) => String(item.id || item.cluster) === String(selectedClusterId),
      )
    ) {
      setSelectedClusterId(
        displayData.length > 0 ? (displayData[0].id || displayData[0].cluster) : null,
      );
    }
  }, [
    isBM,
    user,
    contextData,
    sortDirection,
    stopsSortDirection, // Add stopsSortDirection as dependency
    selectedClusterId,
    setSelectedClusterId,
    setAppError,
    setAppLoading,
    appLoading,
    appError,
  ]); 

  const isScheduleAssigned = (schedule) => !!(schedule && schedule.assigned_to);

  const openAssignModal = (schedule) => {
    setSelectedScheduleForAssign(schedule);
    setIsAssignModalOpen(true);
  };

  const handleAssignComplete = () => {
    setIsAssignModalOpen(false);
    // Refetch data after assigning
    if (isBM && Array.isArray(contextData)) {
      // Trigger a refetch indirectly if needed, or update contextData
      // For now, we'll rely on potential navigation or parent component re-fetch
      // A more robust way might involve a dedicated refetch function in AppContext
      console.log(
        "Assignment complete, consider refetching context data if needed.",
      );
      // If GeneratePage sets data, that might be enough.
      // Or force refresh:
      // window.location.reload(); // Simple but disruptive
    } else if (!isBM) {
      // Fetch RSM schedules again
      const fetchRsmSchedules = async () => {
        setLocalLoading(true);
        try {
          const response = await axios.get("/schedules");
          setDisplayData(sortData(response.data || [], sortDirection, stopsSortDirection));
        } catch (err) {
          /* handle error */
        } finally {
          setLocalLoading(false);
        }
      };
      fetchRsmSchedules();
    }
  };

  const handleUnassign = async (scheduleToUnassign) => {
    if (!scheduleToUnassign || !scheduleToUnassign.cluster) return;

    setActionTargetId(scheduleToUnassign.cluster);
    setUnassignLoading(true);
    setLocalError(null);

    try {
      await axios.put(`/schedules/${scheduleToUnassign.cluster}`, {
        assigned_to_id: null,
      });

      toast({
        title: "Success!",
        description: `Schedule '${scheduleToUnassign.metadata?.itinerary_name || scheduleToUnassign.cluster}' unassigned.`,
      });

      // Update local state immediately for BM
      if (isBM) {
        const updatedContextData = contextData.map((item) =>
          (item.id || item.cluster) === (scheduleToUnassign.id || scheduleToUnassign.cluster)
            ? { ...item, assigned_to: null, assigned_at: null }
            : item,
        );
        setContextData(updatedContextData); // Update context
        setDisplayData(sortData(updatedContextData, sortDirection, stopsSortDirection)); // Update local display data
      } else {
        // Refetch for RSM
        handleAssignComplete(); // Re-use the refetch logic
      }
    } catch (error) {
      console.error("Error unassigning schedule:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to unassign schedule";
      setLocalError(errorMsg);
      toast({
        title: "Unassignment Failed",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setUnassignLoading(false);
      setActionTargetId(null);
    }
  };

  // Handle schedule deletion
  const handleDeleteSchedule = async (scheduleToDelete) => {
    if (!scheduleToDelete || !scheduleToDelete.cluster) return;

    setActionTargetId(scheduleToDelete.cluster);
    setDeleteLoading(true);
    setLocalError(null);

    try {
      const response = await axios.delete(`/schedules/${scheduleToDelete.cluster}`);
      
      if (response.status === 200 || response.status === 204) {
        toast({
          title: "Success!",
          description: `Schedule '${scheduleToDelete.metadata?.itinerary_name || scheduleToDelete.cluster}' deleted successfully.`,
        });

        // Update local state immediately for BM
        if (isBM) {
          const updatedContextData = contextData.filter((item) =>
            (item.id || item.cluster) !== (scheduleToDelete.id || scheduleToDelete.cluster)
          );
          setContextData(updatedContextData);
          setDisplayData(sortData(updatedContextData, sortDirection, stopsSortDirection));
        } else {
          // Refetch for RSM
          handleAssignComplete(); // Re-use the refetch logic
        }

        // Clear selection if deleted schedule was selected
        if (String(selectedClusterId) === String(scheduleToDelete.cluster)) {
          const remainingSchedules = isBM ? 
            contextData.filter(item => (item.id || item.cluster) !== (scheduleToDelete.id || scheduleToDelete.cluster)) :
            displayData.filter(item => (item.id || item.cluster) !== (scheduleToDelete.id || scheduleToDelete.cluster));
          
          if (remainingSchedules.length > 0) {
            setSelectedClusterId(remainingSchedules[0].id || remainingSchedules[0].cluster);
          } else {
            setSelectedClusterId(null);
          }
        }
      }
    } catch (error) {
      console.error("Error deleting schedule:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to delete schedule";
      setLocalError(errorMsg);
      toast({
        title: "Deletion Failed",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setDeleteLoading(false);
      setActionTargetId(null);
      setScheduleToDelete(null);
    }
  };

  const sortData = (dataToSort, direction, stopsDirection) => {
    if (!Array.isArray(dataToSort)) {
      console.error("sortData received non-array:", dataToSort);
      return [];
    }
    
    console.log(`Sorting ${dataToSort.length} items`);
    
    try {
      return [...dataToSort].sort((a, b) => {
        // Sort by number of stops if stopsDirection is set
        if (stopsDirection) {
          const stopsA = a.itinerary?.customers?.length || 0;
          const stopsB = b.itinerary?.customers?.length || 0;
          
          return stopsDirection === 'asc' ? stopsA - stopsB : stopsB - stopsA;
        }
        
        // Otherwise sort by priority as before
        const order = { high: 3, medium: 2, low: 1 };
        const aP = order[a.priority?.toLowerCase()] || 0;
        const bP = order[b.priority?.toLowerCase()] || 0;

        if (aP !== bP) {
          return direction === "asc" ? aP - bP : bP - aP;
        }

        // Secondary sort (e.g., by creation date descending)
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA; // Descending date
      });
    } catch (error) {
      console.error("Error in sortData:", error);
      return dataToSort; // Return unsorted data if sorting fails
    }
  };

  // Add function to toggle stops sort
  const toggleStopsSort = () => {
    let newStopsSortDirection;
    if (!stopsSortDirection) {
      newStopsSortDirection = 'desc'; // First click should be descending (most stops first)
    } else if (stopsSortDirection === 'desc') {
      newStopsSortDirection = 'asc'; // Second click should be ascending (fewest stops first)
    } else {
      newStopsSortDirection = 'desc'; // Third and subsequent clicks alternate back to descending
    }
    
    setStopsSortDirection(newStopsSortDirection);
    
    // Resort the data
    if (isBM) {
      const dataToSort = Array.isArray(contextData) ? contextData : [];
      setDisplayData(sortData(dataToSort, sortDirection, newStopsSortDirection));
    } else {
      // For RSM, refetch or resort existing data
      const dataToSort = [...displayData];
      setDisplayData(sortData(dataToSort, sortDirection, newStopsSortDirection));
    }
  };

  // Helper to get unsaved schedules count
  const unsavedSchedulesCount = useMemo(() => {
    return displayData.filter(item => !item.is_saved).length;
  }, [displayData]);

  const selectedScheduleData = useMemo(() => {
    if (!selectedClusterId || !Array.isArray(displayData)) return null;
    return (
      displayData.find(
        (item) => String(item.id || item.cluster) === String(selectedClusterId),
      ) || null
    );
  }, [selectedClusterId, displayData]);

  // Create directionsRequest for route optimization similar to BuildItineraryPage
  const directionsRequest = useMemo(() => {
    if (!selectedScheduleData?.itinerary?.customers) return null;
    
    const customers = selectedScheduleData.itinerary.customers;
    if (customers.length < 1) return null;

    const waypoints = customers
      .map((c) => ({ location: c.location }))
      .filter((wp) => wp.location);

    if (waypoints.length < 1) return null;

    // Create optimal route connecting all customer stops in sequence
    if (waypoints.length === 1) {
      // Single customer - no route needed
      return null;
    } else if (waypoints.length === 2) {
      // Two customers - simple point-to-point route
      return {
        origin: waypoints[0],
        destination: waypoints[1],
        waypoints: [],
        optimizeWaypoints: false,
        travelMode: "DRIVING",
        routeType: "simple"
      };
    } else {
      // Multiple customers - route through all stops in order
      return {
        origin: waypoints[0],
        destination: waypoints[waypoints.length - 1], 
        waypoints: waypoints.slice(1, -1), // All customers between first and last
        optimizeWaypoints: true, // Let routing service optimize the order of intermediate stops
        travelMode: "DRIVING",
        routeType: "optimized"
      };
    }
  }, [selectedScheduleData]);


  const getPriorityBadgeVariant = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive";
      case "medium":
        return "secondary"; // Changed from yellow to secondary for better contrast
      case "low":
        return "outline"; // Changed from default (dark) to outline
      default:
        return "outline";
    }
  };

  const handlePrevNext = (direction) => {
    const idx = displayData.findIndex(
      (x) => String(x.cluster) === String(selectedClusterId),
    );
    if (idx === -1 || displayData.length <= 1) return;
    let next =
      direction === "prev"
        ? idx === 0
          ? displayData.length - 1
          : idx - 1
        : idx === displayData.length - 1
          ? 0
          : idx + 1;
    setSelectedClusterId(displayData[next].cluster);
  };

  const isLoading = appLoading; // BM directly uses context loading
  const currentError = appError; // BM directly uses context error

  const handleToggleSave = async (scheduleId, isSaved) => {
    try {
      const success = await toggleSaveSchedule(scheduleId, isSaved);
      // Note: contextData update is now handled by useSavedSchedules hook
      // The useEffect with contextData dependency will automatically update displayData
      return success;
    } catch (error) {
      console.error("Error toggling save state:", error);
      return false;
    }
  };

  // Bulk selection handlers
  const toggleBulkSelectionMode = () => {
    const newMode = !bulkSelectionMode;
    setBulkSelectionMode(newMode);
    setSelectedSchedules(new Set()); // Clear selections when toggling mode
    
    // If turning off bulk mode, ensure we're not in a weird UI state
    if (!newMode) {
      // Reset any bulk-related UI state if needed
    }
  };

  const handleScheduleSelection = (scheduleId) => {
    const newSelected = new Set(selectedSchedules);
    if (newSelected.has(scheduleId)) {
      newSelected.delete(scheduleId);
    } else {
      newSelected.add(scheduleId);
    }
    setSelectedSchedules(newSelected);
  };

  const selectAllSchedules = () => {
    const unsavedSchedules = displayData
      .filter(item => !item.is_saved)
      .map(item => item.id || item.cluster);
    setSelectedSchedules(new Set(unsavedSchedules));
  };

  const selectAllSchedulesForDelete = () => {
    const allSchedules = displayData.map(item => item.id || item.cluster);
    setSelectedSchedules(new Set(allSchedules));
  };

  const clearAllSelections = () => {
    setSelectedSchedules(new Set());
  };

  const handleBulkSave = async () => {
    if (selectedSchedules.size === 0) {
      toast({
        title: "No schedules selected",
        description: "Please select at least one schedule to save",
        variant: "destructive"
      });
      return;
    }

    setBulkSaveLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process selections sequentially to avoid race conditions with context updates
      const scheduleIds = Array.from(selectedSchedules);
      console.log(`Starting bulk save for ${scheduleIds.length} schedules:`, scheduleIds);
      
      for (const scheduleId of scheduleIds) {
        try {
          console.log(`Saving schedule: ${scheduleId}`);
          const success = await toggleSaveSchedule(scheduleId, true);
          if (success) {
            successCount++;
            console.log(`Successfully saved schedule: ${scheduleId}`);
          } else {
            errorCount++;
            console.log(`Failed to save schedule: ${scheduleId}`);
          }
        } catch (error) {
          console.error(`Error saving schedule ${scheduleId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast({
          title: "Bulk save completed",
          description: `Successfully saved ${successCount} schedule${successCount !== 1 ? 's' : ''}${errorCount > 0 ? `. ${errorCount} failed.` : ''}`,
        });
      }

      if (errorCount > 0 && successCount === 0) {
        toast({
          title: "Bulk save failed",
          description: `Failed to save ${errorCount} schedule${errorCount !== 1 ? 's' : ''}`,
          variant: "destructive"
        });
      }

      // Clear selections and exit bulk mode
      setSelectedSchedules(new Set());
      setBulkSelectionMode(false);

    } catch (error) {
      console.error("Error during bulk save:", error);
      toast({
        title: "Bulk save error",
        description: "An unexpected error occurred during bulk save",
        variant: "destructive"
      });
    } finally {
      setBulkSaveLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSchedules.size === 0) {
      toast({
        title: "No schedules selected",
        description: "Please select at least one schedule to delete",
        variant: "destructive"
      });
      return;
    }

    setBulkDeleteLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process deletions sequentially to avoid race conditions with context updates
      const scheduleIds = Array.from(selectedSchedules);
      console.log(`Starting bulk delete for ${scheduleIds.length} schedules:`, scheduleIds);
      
      for (const scheduleId of scheduleIds) {
        try {
          console.log(`Deleting schedule: ${scheduleId}`);
          const response = await axios.delete(`/schedules/${scheduleId}`);
          
          if (response.status === 200 || response.status === 204) {
            successCount++;
            console.log(`Successfully deleted schedule: ${scheduleId}`);
          } else {
            errorCount++;
            console.log(`Failed to delete schedule: ${scheduleId}`);
          }
        } catch (error) {
          console.error(`Error deleting schedule ${scheduleId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        // Update context data to remove deleted schedules
        if (isBM && contextData) {
          const updatedContextData = contextData.filter((item) =>
            !selectedSchedules.has(item.id || item.cluster)
          );
          setContextData(updatedContextData);
          setDisplayData(sortData(updatedContextData, sortDirection, stopsSortDirection));
        } else {
          // Refetch for RSM
          handleAssignComplete();
        }

        // Clear selection if deleted schedule was selected
        if (selectedSchedules.has(selectedClusterId)) {
          const remainingSchedules = isBM ? 
            contextData.filter(item => !selectedSchedules.has(item.id || item.cluster)) :
            displayData.filter(item => !selectedSchedules.has(item.id || item.cluster));
          
          if (remainingSchedules.length > 0) {
            setSelectedClusterId(remainingSchedules[0].id || remainingSchedules[0].cluster);
          } else {
            setSelectedClusterId(null);
          }
        }

        toast({
          title: "Bulk delete completed",
          description: `Successfully deleted ${successCount} schedule${successCount !== 1 ? 's' : ''}${errorCount > 0 ? `. ${errorCount} failed.` : ''}`,
        });
      }

      if (errorCount > 0 && successCount === 0) {
        toast({
          title: "Bulk delete failed",
          description: `Failed to delete ${errorCount} schedule${errorCount !== 1 ? 's' : ''}`,
          variant: "destructive"
        });
      }

      // Clear selections and exit bulk mode
      setSelectedSchedules(new Set());
      setBulkSelectionMode(false);

    } catch (error) {
      console.error("Error during bulk delete:", error);
      toast({
        title: "Bulk delete error",
        description: "An unexpected error occurred during bulk delete",
        variant: "destructive"
      });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // console.log("ClusterListPage displayData:", displayData); // Disabled for brevity
  return (
    <div>
      <Header />
      
      {/* All Schedules Overview Map */}
      {displayData && displayData.length > 0 && (
        <Card className="overflow-hidden h-[400px] mb-6">
          <CardHeader className="p-4 border-b">
            <CardTitle className="text-lg font-semibold">
              All Generated Schedules Overview Map
            </CardTitle>
            <Text className="text-sm text-muted-foreground">
              Showing all customer stops across {displayData.length} generated schedules
            </Text>
          </CardHeader>
          <CardContent className="p-0 h-full">
            <AllSchedulesMapSection schedules={displayData} />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {currentError && (
            <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-md">
              {currentError}
            </div>
          )}
          {isLoading &&
            !currentError && ( // Show loading only if no error
              <div className="flex items-center justify-center h-96 border rounded-md bg-muted/20">
                <Loader2 className="animate-spin h-8 w-8 text-primary" />
              </div>
            )}

          {!isLoading && !currentError && selectedScheduleData && (
            <Card className="overflow-hidden">
              <CardHeader className="flex flex-row flex-wrap justify-between items-start p-4 gap-2">
                <div className="flex-grow min-w-[200px]">
                  <CardTitle className="text-lg font-semibold">
                    {selectedScheduleData.metadata?.itinerary_name ||
                      `Schedule ${selectedScheduleData.cluster}`}
                  </CardTitle>
                </div>
                {isBM && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isScheduleAssigned(selectedScheduleData) ? (
                      <>
                        <Badge
                          className="flex items-center gap-1.5 py-1 px-2"
                          variant="secondary"
                          title={`Assigned to ${selectedScheduleData.assigned_to?.full_name || "RSM"}`}
                        >
                          <Check className="h-3 w-3" />
                          <span className="hidden sm:inline">
                            Assigned to
                          </span>{" "}
                          {selectedScheduleData.assigned_to?.full_name || "RSM"}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAssignModal(selectedScheduleData)}
                          disabled={
                            unassignLoading &&
                            actionTargetId === selectedScheduleData.cluster
                          }
                          className="h-7 px-2"
                          title="Reassign RSM"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnassign(selectedScheduleData)}
                          disabled={
                            unassignLoading &&
                            actionTargetId === selectedScheduleData.cluster
                          }
                          className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Unassign RSM"
                        >
                          {unassignLoading &&
                          actionTargetId === selectedScheduleData.cluster ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <UserMinus className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAssignModal(selectedScheduleData)}
                        className="h-7 px-2"
                        title="Assign to RSM"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Assign RSM
                      </Button>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="h-[400px] border rounded-md overflow-hidden bg-muted/10">
                  <MapSection
                    customers={selectedScheduleData.itinerary?.customers}
                    directionsRequest={directionsRequest}
                  />
                </div>
                <Separator />
                <div className="flex items-stretch gap-4 w-full">
                  <div className="flex flex-1 gap-4">
                    <div className="flex flex-col items-center justify-center flex-1 p-3 border rounded-md min-w-[100px] max-w-[140px] text-center bg-background">
                      <span className="text-xs text-muted-foreground mb-1">
                        Priority
                      </span>
                      <Badge
                        variant={getPriorityBadgeVariant(
                          selectedScheduleData.priority,
                        )}
                        className="capitalize"
                      >
                        {selectedScheduleData.priority || "N/A"}
                      </Badge>
                    </div>
                    <div className="flex flex-col items-center justify-center flex-1 p-3 border rounded-md min-w-[100px] max-w-[140px] text-center bg-background">
                      <span className="text-xs text-muted-foreground mb-1">
                        Stops
                      </span>
                      <span className="font-semibold text-xl">
                        {selectedScheduleData.itinerary?.customers?.length || 0}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <Button
                      variant="default"
                      size="default"
                      onClick={() =>
                        navigate(`/schedules/${selectedScheduleData.cluster}`)
                      }
                      className="w-full min-w-[140px] max-w-[180px]"
                    >
                      View Schedule
                    </Button>
                  </div>
                </div>
                <div className="flex justify-center gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrevNext("prev")}
                    disabled={displayData.length <= 1}
                    className="px-4"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="ml-1">Prev</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrevNext("next")}
                    disabled={displayData.length <= 1}
                    className="px-4"
                  >
                    <span className="mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {!isLoading &&
            !currentError &&
            !selectedScheduleData &&
            displayData.length > 0 && (
              <div className="text-center py-10 text-muted-foreground">
                Please select a schedule from the list.
              </div>
            )}

          {/* Added condition for no schedules generated */}
          {!isLoading && !currentError && displayData.length === 0 && (
            <div className="text-center py-10 text-muted-foreground">
              No schedules generated. Go to the{" "}
              <Link to="/" className="text-primary hover:underline">
                Generate
              </Link>{" "}
              page to create some.
            </div>
          )}
        </div>
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader className="p-4">
              <div className="flex justify-between items-center mb-2">
                <CardTitle className="text-lg font-semibold">
                  Generated Schedules ({displayData.length})
                  {isBM && bulkSelectionMode && (
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      • {unsavedSchedulesCount} unsaved
                    </span>
                  )}
                </CardTitle>
                <div className="flex gap-2">
                  {isBM && (
                    <Button
                      variant={bulkSelectionMode ? "default" : "outline"}
                      size="sm"
                      onClick={toggleBulkSelectionMode}
                      className="h-8 px-3"
                    >
                      <CheckSquare className="h-4 w-4 mr-1" />
                      {bulkSelectionMode ? "Cancel" : "Select"}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleStopsSort}
                    className={cn(
                      "h-8 px-2",
                      stopsSortDirection && "bg-muted"
                    )}
                    title={stopsSortDirection 
                      ? `Sorting by stops ${stopsSortDirection === 'asc' ? 'ascending' : 'descending'}`
                      : 'Sort by number of stops'}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    <span className="sr-only">Sort by stops</span>
                  </Button>
                </div>
              </div>
              
              {/* Bulk selection controls */}
              {isBM && bulkSelectionMode && (
                <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/30 rounded-md">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllSchedules}
                      disabled={displayData.filter(item => !item.is_saved).length === 0}
                    >
                      Select All Unsaved
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllSchedulesForDelete}
                      disabled={displayData.length === 0}
                      className="text-destructive hover:text-destructive"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllSelections}
                      disabled={selectedSchedules.size === 0}
                    >
                      Clear All
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-sm text-muted-foreground">
                      {selectedSchedules.size} selected
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={selectedSchedules.size === 0 || bulkDeleteLoading}
                        >
                          {bulkDeleteLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete ({selectedSchedules.size})
                            </>
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Delete Selected Schedules
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete {selectedSchedules.size} selected schedule{selectedSchedules.size !== 1 ? 's' : ''}? 
                            This action cannot be undone and will permanently remove all selected schedules and their data.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleBulkDelete}
                            disabled={bulkDeleteLoading}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {bulkDeleteLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Deleting...
                              </>
                            ) : (
                              `Delete ${selectedSchedules.size} Schedule${selectedSchedules.size !== 1 ? 's' : ''}`
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleBulkSave}
                      disabled={selectedSchedules.size === 0 || bulkSaveLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {bulkSaveLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Bookmark className="h-4 w-4 mr-1" />
                          Save ({selectedSchedules.size})
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(80vh-8rem)]">
                <div className="divide-y">
                  {isLoading ? (
                    <div className="p-4 text-center text-muted-foreground">
                      Loading schedules...
                    </div>
                  ) : currentError ? (
                    <div className="p-4 text-center text-red-600">
                      Error: {currentError}
                    </div>
                  ) : displayData.length > 0 ? (
                    displayData.map((item, idx) => {
                      // Only render visible items for performance (virtual scrolling would be better)
                      if (idx > 100) {
                        console.warn(`Rendering limited to 100 items for performance. Total items: ${displayData.length}`);
                        return null;
                      }
                      
                      const progress = (item.itinerary?.customers && item.itinerary.customers.length > 0)
                        ? Math.round((item.itinerary.customers.filter(c => c.status === "COMPLETED").length + 0.5 * item.itinerary.customers.filter(c => c.status === "IN_PROGRESS").length) / item.itinerary.customers.length * 100)
                        : 0;
                      // Debug: Log the first few items to see available fields
                      if (idx < 2) {
                        console.log("Schedule item fields:", Object.keys(item));
                        console.log("Full item data:", item);
                      }
                      
                      const scheduleId = item.id || item.cluster;
                      const isSelected = selectedSchedules.has(scheduleId);
                      const isCurrentlySelected = String(selectedClusterId) === String(scheduleId);
                      
                      return (
                        <div
                          key={scheduleId}
                          className={`relative p-4 transition-colors ${
                            bulkSelectionMode 
                              ? isSelected 
                                ? "bg-blue-50 border-l-4 border-l-blue-500 cursor-pointer" 
                                : "hover:bg-muted/30 cursor-pointer"
                              : `cursor-pointer hover:bg-muted/50 ${
                                  isCurrentlySelected ? "bg-muted" : ""
                                }`
                          }`}
                          onClick={bulkSelectionMode 
                            ? () => handleScheduleSelection(scheduleId)
                            : () => setSelectedClusterId(scheduleId)
                          }
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-start gap-3">
                              {/* Bulk selection checkbox */}
                              {isBM && bulkSelectionMode && (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleScheduleSelection(scheduleId)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-0.5"
                                />
                              )}
                              <div className="font-medium text-sm">
                                {item.metadata?.itinerary_name || `Schedule ${item.cluster}`}
                                {item.is_saved && (
                                  <Bookmark className="inline ml-2 h-3 w-3 text-gray-400 fill-gray-400" />
                                )}
                              </div>
                            </div>
                            
                            {/* Show SaveScheduleButton only when not in bulk selection mode */}
                            {isBM && !bulkSelectionMode && (
                              <SaveScheduleButton
                                scheduleId={scheduleId}
                                isSaved={item.is_saved}
                                onToggleSave={handleToggleSave}
                                size="xs"
                                showText={false}
                                className="h-6 w-6 p-1 z-10"
                              />
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-2 items-center text-xs text-muted-foreground mb-1">
                            <span>Region: {item.metadata?.region || "N/A"}</span>
                            <span>Stops: {item.itinerary?.customers?.length || 0}</span>
                            {item.assigned_to ? (
                              <span className="flex items-center gap-1">
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 flex items-center gap-0.5">
                                  <Check size={10} /> {item.assigned_to.full_name}
                                </Badge>
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 flex items-center gap-0.5">
                                  Unassigned
                                </Badge>
                              </span>
                            )}
                            <Badge
                              variant={getPriorityBadgeVariant(item.priority)}
                              className="uppercase text-[10px] px-1.5 py-0.5"
                            >
                              {item.priority || "N/A"}
                            </Badge>
                            {item.created_at && (
                              <span className="flex items-center gap-1">
                                <Clock size={10} className="text-muted-foreground" />
                                {formatDate(item.created_at, "MM/dd/yyyy")}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex justify-between items-center gap-2 mt-1">
                            <div className="flex-1">
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Progress</span>
                                <span>{progress}%</span>
                              </div>
                              <div className="w-full"><div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-2 bg-blue-500" style={{ width: `${progress}%` }} /></div></div>
                            </div>
                            
                            {/* RSM assignment controls - styled to match app design but with visibility fixes */}
                            {isBM && (
                              <div className="z-20">
                                {item.assigned_to ? (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="outline"
                                      size="xs"
                                      className="h-7 w-7 p-0 bg-background"
                                      title="Reassign RSM"
                                      onClick={e => { e.stopPropagation(); openAssignModal(item); }}
                                    >
                                      <RefreshCw className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="xs"
                                      className="h-7 w-7 p-0 text-destructive bg-background"
                                      title="Unassign RSM"
                                      disabled={unassignLoading && actionTargetId === item.cluster}
                                      onClick={e => { e.stopPropagation(); handleUnassign(item); }}
                                    >
                                      {unassignLoading && actionTargetId === item.cluster ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <UserMinus className="h-3.5 w-3.5" />
                                      )}
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="xs"
                                          className="h-7 w-7 p-0 text-destructive bg-background hover:bg-destructive hover:text-destructive-foreground"
                                          title="Delete Schedule"
                                          onClick={e => e.stopPropagation()}
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle className="flex items-center gap-2">
                                            <AlertTriangle className="h-5 w-5 text-destructive" />
                                            Delete Schedule
                                          </AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Are you sure you want to delete "{item.metadata?.itinerary_name || `Schedule ${item.cluster}`}"? 
                                            This action cannot be undone and will permanently remove the schedule and all its data.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleDeleteSchedule(item)}
                                            disabled={deleteLoading && actionTargetId === item.cluster}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            {deleteLoading && actionTargetId === item.cluster ? (
                                              <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Deleting...
                                              </>
                                            ) : (
                                              "Delete"
                                            )}
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 px-2 bg-background"
                                      title="Assign to RSM"
                                      onClick={e => { e.stopPropagation(); openAssignModal(item); }}
                                    >
                                      <UserPlus className="h-4 w-4 mr-1" /> Assign RSM
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="xs"
                                          className="h-7 w-7 p-0 text-destructive bg-background hover:bg-destructive hover:text-destructive-foreground"
                                          title="Delete Schedule"
                                          onClick={e => e.stopPropagation()}
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle className="flex items-center gap-2">
                                            <AlertTriangle className="h-5 w-5 text-destructive" />
                                            Delete Schedule
                                          </AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Are you sure you want to delete "{item.metadata?.itinerary_name || `Schedule ${item.cluster}`}"? 
                                            This action cannot be undone and will permanently remove the schedule and all its data.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleDeleteSchedule(item)}
                                            disabled={deleteLoading && actionTargetId === item.cluster}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            {deleteLoading && actionTargetId === item.cluster ? (
                                              <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Deleting...
                                              </>
                                            ) : (
                                              "Delete"
                                            )}
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Selection indicator */}
                          {String(selectedClusterId) === String(item.cluster) && (
                            <div className="absolute inset-0 border border-primary/30 rounded pointer-events-none" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-4 text-center text-muted-foreground">
                      No schedules generated yet.
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
            {!isLoading && !currentError && displayData.length > 0 && (
              <div className="p-4 border-t">
                {isBM && (
                  <FeedbackComponent
                    question="Were these generated schedules helpful?"
                    showRatings={true}
                    feedback={clusterGroupingsFeedback}
                    setFeedback={setClusterGroupingsFeedback}
                    feedbackKey="cluster-groupings"
                    context={feedbackContext} // <-- Pass context here
                    ratingLabels={[
                      "Grouping Accuracy",
                      "Opportunity Coverage",
                      "Schedule Efficiency",
                      "Overall Satisfaction",
                    ]}
                  />
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
      {isAssignModalOpen && selectedScheduleForAssign && (
        <AssignRsmModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          clusterId={selectedScheduleForAssign.cluster}
          clusterName={selectedScheduleForAssign.metadata?.itinerary_name}
          onAssignComplete={handleAssignComplete}
        />
      )}
    </div>
  );
}

export default ClusterListPage;
